/**
 * WHICH COUNTRIES HAVE A SEO LANDING PAGE, decided in exactly one place.
 *
 * Pure and browser-safe. A country page exists ONLY for a country listed here:
 * a country appearing in the data is never enough on its own, because a page
 * for a country with two listings is thin content and Google judges the whole
 * site by its thinnest pages. Adding a country is a deliberate edit to this list
 * after a person has looked at its real inventory.
 *
 * The country CODE is the identity and the SLUG is a stable English URL segment
 * written here by hand. It is never derived from a display name, so a
 * translated or renamed country can never move a URL that Google has indexed.
 */

export type SeoCountry = {
    /** ISO 3166-1 alpha-2, as stored in `listings.country_code`. */
    code: string
    /** The URL segment under `/buy/`. Lowercase, English, permanent. */
    slug: string
    name: string
    /** The page-name half of the title. `<Seo>` appends " - Bazio". */
    title: string
    description: string
    heading: string
    intro: string
    /**
     * A person reviewed this country's inventory and approved it for indexing.
     * It lowers the automatic threshold (see `countryIndexDecision`); it never
     * makes an empty country indexable.
     */
    approved: boolean
    /** Codes of nearby countries to suggest at the bottom of the page. */
    related: Array<string>
}

export const SEO_COUNTRIES: ReadonlyArray<SeoCountry> = [
    {
        code: 'AL',
        slug: 'albania',
        name: 'Albania',
        title: 'Buy & Sell in Albania · Marketplace Listings',
        description: 'Browse items for sale in Albania on Bazio. Discover electronics, vehicles, services, digital products and more from sellers and marketplace communities across Albania.',
        heading: 'Buy & Sell in Albania',
        intro: 'Discover items from Bazio sellers and marketplace communities across Albania. Browse current listings, find what you need and contact sellers directly.',
        approved: true,
        related: ['XK']
    },
    {
        code: 'XK',
        slug: 'kosovo',
        name: 'Kosovo',
        title: 'Buy & Sell in Kosovo · Marketplace Listings',
        description: 'Browse items for sale in Kosovo on Bazio. Discover products and services from sellers and marketplace communities across Kosovo.',
        heading: 'Buy & Sell in Kosovo',
        intro: 'Discover items from Bazio sellers and marketplace communities across Kosovo. Browse current listings and connect directly with sellers.',
        approved: true,
        related: ['AL']
    }
]

export const seoCountryBySlug = (slug: string | null | undefined): SeoCountry | null =>
    SEO_COUNTRIES.find((country) => country.slug === (slug ?? '').toLowerCase()) ?? null

export const seoCountryByCode = (code: string | null | undefined): SeoCountry | null =>
    SEO_COUNTRIES.find((country) => country.code === (code ?? '').toUpperCase()) ?? null

/**
 * By the canonical English country NAME, as `marketplace_communities.country`
 * holds it (the database writes that name from `country_code`, so for a
 * configured country the two always agree). For a surface that carries the
 * name but not the code.
 */
export const seoCountryByName = (name: string | null | undefined): SeoCountry | null => {
    const needle = (name ?? '').trim().toLowerCase()

    return needle ? SEO_COUNTRIES.find((country) => country.name.toLowerCase() === needle) ?? null : null
}

/** The page's own address, `/buy/<slug>`, optionally narrowed and paged. */
export const countryPagePath = (
    country: Pick<SeoCountry, 'slug'>,
    options: { page?: number, category?: string | null } = {}
): string => {
    const params = new URLSearchParams()

    if (options.category) params.set('category', options.category)
    if (options.page && options.page > 1) params.set('page', String(options.page))

    const query = params.toString()

    return `/buy/${country.slug}${query ? `?${query}` : ''}`
}

/**
 * THE INVENTORY THRESHOLDS, in distinct Store items, never raw posts.
 *
 *   0             no page at all (404)
 *   1 to 4        page exists, noindex
 *   5 to 9        noindex unless a person approved the country
 *   10 and over   indexable
 */
export const SEO_INDEX_MIN_ITEMS = 10
export const SEO_APPROVED_MIN_ITEMS = 5

export type CountryIndexDecision = 'not_found' | 'noindex' | 'index'

export const countryIndexDecision = (country: Pick<SeoCountry, 'approved'>, itemCount: number): CountryIndexDecision => {
    if (itemCount <= 0) return 'not_found'
    if (itemCount >= SEO_INDEX_MIN_ITEMS) return 'index'
    if (country.approved && itemCount >= SEO_APPROVED_MIN_ITEMS) return 'index'

    return 'noindex'
}

/**
 * THE LOCATION QUALITY RULE for SEO pages.
 *
 * A listing counts towards a country page only when its country is known well
 * enough to promise it to a search engine: a person's correction, or any
 * detected answer at 0.90 or above (an explicit place in the listing, or the
 * marketplace's own country). A seller-chosen place the place list did not
 * recognise is held back until an admin corrects it, whatever its fallback
 * says. `COUNTRY_SEO_LOCATION_SQL` is the same rule for the database.
 */
export const SEO_MIN_LOCATION_CONFIDENCE = 0.9

export const isSeoReliableLocation = (location: {
    source: string | null
    confidence: number | null
    unresolved?: string | null
}): boolean => {
    if (location.unresolved) return false
    if (location.source === 'admin') return true

    return typeof location.confidence === 'number' && location.confidence >= SEO_MIN_LOCATION_CONFIDENCE
}

/** The SQL half of `isSeoReliableLocation`, on the `l` listings alias. */
export const COUNTRY_SEO_LOCATION_SQL = `(l.location_unresolved IS NULL AND (l.location_source = 'admin' OR l.location_confidence >= ${SEO_MIN_LOCATION_CONFIDENCE.toFixed(2)}))`

/**
 * The country page a listing may link to, or null.
 *
 * Only a configured country, only a location reliable enough for SEO, and only
 * for a listing that is itself live: a link from a sold listing to "browse
 * Albania" is fine for a visitor, but a link from a hidden one is not.
 */
export const seoCountryLinkFor = (location: {
    countryCode: string | null
    source: string | null
    confidence: number | null
    unresolved?: string | null
}): { name: string, path: string } | null => {
    const country = seoCountryByCode(location.countryCode)

    if (!country || !isSeoReliableLocation(location)) return null

    return { name: country.name, path: countryPagePath(country) }
}
